import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../../../authConfig";
import { useApi } from "@services/useApi";
import { NavItems } from "@helpers/navigation";
import { AssessmentShell } from "@fassessment/components/AssessmentShell";
import { LBtn, LSubTitle, LTitle, LButtonGroup } from "@components";
import { LanguageId } from "@store/generalTypes";
import { GlobalSettings } from "@store/globalSettingsSlice";
import { useAppSelector } from "@app/hooks";
import { LNotification } from "@components/LNotification";
import { useState } from "react";

export interface StorageAssessmentForm {
    assessmentFormId: number;
    assessmentId: number | undefined;
    flow: number;
    language: LanguageId;
    examineeName: string;
    globalSettings: GlobalSettings;
}

const AssessmentWelcome = (): JSX.Element => {
    // TODO: take from query
    const SHOW_SHORTCUTS = false;

    const [error, setError] = useState("");

    const msalInstance = new PublicClientApplication(msalConfig);

    const { initializeAssessment, loading } = useApi();

    const { globalSettings, startAssessment, examinee } = useAppSelector((x) => {
        return { globalSettings: x.globalSettings, startAssessment: x.startAssessment, examinee: x.newExaminee };
    });

    const initializeAndNavigate = async (path: string, flow: number, language = LanguageId.en): Promise<void> => {
        const assessment = await initializeAssessment(
            startAssessment.locationId,
            startAssessment.examineeId,
            language,
            startAssessment.assessmentFormId,
            startAssessment.upcomingAssessmentId,
            startAssessment.isRetest
        ).catch((data) => {
            // TODO: fix error handler
            setError("ERROR");
        });

        const assessmentForm: StorageAssessmentForm = {
            assessmentFormId: startAssessment.assessmentFormId,
            assessmentId: assessment?.id,
            flow,
            language,
            examineeName: `${examinee.contactInfo.firstName} ${examinee.contactInfo.lastName}`,
            globalSettings
        };

        localStorage.setItem("assessmentForm", JSON.stringify(assessmentForm));

        // TODO: create hook
        const logoutRequest = {
            account: msalInstance.getAllAccounts()[0],
            mainWindowRedirectUri: path,
            postLogoutRedirectUri: path
        };

        await msalInstance.logoutRedirect(logoutRequest);
    };

    const handleNavigateToEnglishInstruction = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.Start.path, 1, LanguageId.en);
    };

    const handleNavigateToSpanishInstruction = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.Start.path, 1, LanguageId.es);
    };

    const handleNavigateToDemoPresentTarget = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.Begin.path, 2);
    };

    const handleNavigateToDemoFreeRecall = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.AssessmentFreeRecall.path, 3);
    };

    const handleNavigateToClosingVideo = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.AssessmentClosingVideo.path, 4);
    };

    const handleNavigateToDemoDelayedRecall = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.AssessmentDelayedRecall.path, 5);
    };

    const handleNavigateToDemoCuedRecalls = async (): Promise<void> => {
        await initializeAndNavigate(NavItems.Assessments.AssessmentCuedRecalls.path, 6);
    };

    return (
        <AssessmentShell>
            <div className="hero-body">
                <div className="container has-text-centered">
                    <LSubTitle>Welcome to the</LSubTitle>
                    <LTitle>LASSI-D™</LTitle>
                    <LButtonGroup extraSpacing={true}>
                        <LBtn onClick={handleNavigateToEnglishInstruction} loading={loading} isBold>
                            English
                        </LBtn>
                        <LBtn onClick={handleNavigateToSpanishInstruction} loading={loading} isBold>
                            Español
                        </LBtn>
                    </LButtonGroup>
                    {SHOW_SHORTCUTS && (
                        <LButtonGroup extraSpacing={false}>
                            <LBtn onClick={handleNavigateToDemoPresentTarget} loading={loading}>
                                Demo: Present Target
                            </LBtn>
                            <LBtn onClick={handleNavigateToDemoFreeRecall} loading={loading}>
                                Demo: Free Recall
                            </LBtn>
                            <LBtn onClick={handleNavigateToDemoCuedRecalls} loading={loading}>
                                Demo: Cued Recalls
                            </LBtn>
                            <LBtn onClick={handleNavigateToDemoDelayedRecall} loading={loading}>
                                Demo: Delayed Recall
                            </LBtn>
                            <LBtn onClick={handleNavigateToClosingVideo} loading={loading}>
                                Demo: Closing Video
                            </LBtn>
                        </LButtonGroup>
                    )}
                    <p className="pb-3">Version 3.0.0</p>
                    <p className="pb-3">David A. Loewenstein, PhD and Rosie E. Curiel, PsyD.</p>
                    <p>i-Function, Inc. holds an exclusive license to the LASSI-D™</p>
                    {error && <LNotification>{error}</LNotification>}
                </div>
            </div>
        </AssessmentShell>
    );
};

export default AssessmentWelcome;
